export type ScreenId =
  | "feed"
  | "search"
  | "ad-detail"
  | "product"
  | "comments"
  | "seller"
  | "favorites"
  | "profile"
  | "settings"
  | "notifications"
  | "my-ads"
  | "create-1"
  | "create-2"
  | "create-3"
  | "chats"
  | "chat"
  | "promotions"
  | "moderator";

export interface Listing {
  id: string;
  title: string;
  price: number;
  city: string;
  cityId?: string | null;
  regionCode?: string | null;
  age: string;
  condition: string;
  category: string;
  emoji: string;
  tint: string;
  favorite: boolean;
  status?: "active" | "sold" | "review" | "draft" | "hidden";
  seller: string;
  sellerId?: string;
  sellerRating: number | null;
  productRating?: number | null;
  productReviewsCount?: number;
  commentsEnabled?: boolean;
  commentsCount?: number;
  product: string;
  description?: string;
  imageUrl?: string | null;
}

export interface SellerReview {
  id: string;
  author: { displayName: string; avatarUrl?: string | null };
  rating: number;
  text: string;
  outcome: "bought" | "no_agreement" | "declined";
  createdAt: string;
  updatedAt?: string | null;
  edited: boolean;
  contributesToRating: boolean;
  ad: { id: string; title: string | null; lifecycle: string; available: boolean };
  reply?: { text: string; createdAt: string; updatedAt?: string | null; edited: boolean } | null;
}

export interface SellerProfile {
  seller: { id: string; displayName: string; avatarUrl?: string | null; city?: string | null; memberSince: string };
  counts: { activeAds: number; reviews: number };
  reputation: { average: number | null; reviewersCount: number; reviewsCount: number; distribution: Record<string, number> };
  reviews: SellerReview[];
}

export interface SellerReviewsPage {
  items: SellerReview[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ReviewOpportunity {
  adId: string;
  title: string | null;
  conversationId: string;
  seller: { id: string; displayName: string };
  existingReviewId: string | null;
  canCreate: boolean;
  reasonCode: string | null;
  eligibilityExpiresAt: string | null;
  confirmationType: string;
}

export interface SellerReviewManage {
  reviewId: string;
  version: number;
  status: string;
  editableUntil: string | null;
  actions: { canEdit: boolean; canHide: boolean; canReply: boolean; canEditReply: boolean; canReport: boolean };
}

export interface ModerationReview {
  reviewId: string;
  status: string;
  rating: number;
  text: string;
  author: string;
  seller: string;
  ad: { id: string; title: string };
  reports: Array<{ reason: string; details: string; status: string }>;
}

export interface RegionLocation {
  code: string;
  name: string;
  type: string;
  federalDistrict: string;
}

export interface CityLocation {
  id: string;
  name: string;
  region: Pick<RegionLocation, "code" | "name">;
  isRegionCapital: boolean;
}

export interface CatalogLocationFilter {
  regionCode?: string;
  cityId?: string;
}

export interface ChatAttachment {
  id: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
  url: string;
}

export interface ChatMessage {
  id: string;
  author: "me" | "other";
  text: string;
  time: string;
  pending?: boolean;
  status?: "sent" | "read";
  sequence?: number;
  attachments?: ChatAttachment[];
}

export interface Conversation {
  id: string;
  name: string;
  listing: string;
  listingId?: string;
  preview: string;
  time: string;
  unread: number;
  blockedByMe?: boolean;
  dealStatus?: "open" | "sold" | "closed";
  canMarkSold?: boolean;
  canRate?: boolean;
  isSupport?: boolean;
  messages: ChatMessage[];
}

export interface ChatInboxItem {
  id: number;
  kind: "message";
  conversationId: string;
  listingTitle: string;
  messageSequence: number;
  createdAt: string;
  readAt: string | null;
}

export interface ChatInbox {
  items: ChatInboxItem[];
  unreadCount: number;
}

export interface AdComment {
  id: string;
  author: string;
  text: string;
  parentId?: string | null;
  time: string;
}

export interface Draft {
  title: string;
  price: string;
  category: string;
  city: string;
  cityId: string;
  regionCode: string;
  condition: string;
  description: string;
  delivery: string;
  date: string;
  time: string;
  commentsEnabled?: boolean;
}

export interface PreviewState {
  favorites: string[];
  messages: Record<string, ChatMessage[]>;
  draft: Draft;
  submitted: boolean;
  offline: boolean;
}

export const emptyDraft = (): Draft => ({
  title: "",
  price: "",
  category: "",
  city: "",
  cityId: "",
  regionCode: "",
  condition: "",
  description: "",
  delivery: "Самовывоз",
  date: "",
  time: "",
  commentsEnabled: true,
});

export const seedListings: Listing[] = [
  {
    id: "ad-1",
    title: "Кресло, которое ждёт новый дом",
    price: 12500,
    city: "Москва",
    cityId: "524901",
    regionCode: "GN-48",
    age: "сегодня, 10:42",
    condition: "Хорошее состояние",
    category: "Дом",
    emoji: "⌂",
    tint: "lavender",
    favorite: false,
    seller: "Алина",
    sellerRating: 4.9,
    product: "Кресла и стулья",
  },
  {
    id: "ad-2",
    title: "Плёночная камера для неспешных прогулок",
    price: 8900,
    city: "Санкт-Петербург",
    cityId: "498817",
    regionCode: "GN-66",
    age: "вчера, 19:18",
    condition: "Отличное состояние",
    category: "Техника",
    emoji: "◉",
    tint: "peach",
    favorite: false,
    seller: "Михаил",
    sellerRating: 4.8,
    product: "Плёночные камеры",
  },
  {
    id: "ad-3",
    title: "Настольная лампа, тёплый вечерний свет",
    price: 3200,
    city: "Казань",
    cityId: "551487",
    regionCode: "GN-73",
    age: "16.09.2026, 08:04",
    condition: "Новое",
    category: "Дом",
    emoji: "✦",
    tint: "mint",
    favorite: false,
    seller: "Ирина",
    sellerRating: 5,
    product: "Освещение",
  },
  {
    id: "ad-4",
    title: "Настройка домашнего роутера",
    price: 1500,
    city: "Москва",
    cityId: "524901",
    regionCode: "GN-48",
    age: "сегодня, 09:15",
    condition: "Не применимо",
    category: "Услуги",
    emoji: "⌁",
    tint: "mint",
    favorite: false,
    seller: "Алексей",
    sellerRating: 4.7,
    product: "Компьютерные услуги",
  },
  {
    id: "ad-5",
    title: "Игровой предмет — редкий скин",
    price: 2400,
    city: "Екатеринбург",
    cityId: "1486209",
    regionCode: "GN-66",
    age: "сегодня, 08:50",
    condition: "Новое",
    category: "Электронные товары",
    emoji: "◈",
    tint: "peach",
    favorite: false,
    seller: "Денис",
    sellerRating: 4.6,
    product: "Игровые предметы",
  },
];

export const formatMoney = (value: number): string =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value) + " ₽";

export const formatDate = (iso: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : "дд.мм.гггг";
};

const timeFormatter = new Intl.DateTimeFormat("ru-RU", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export const formatTime = (value: string): string => {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(value);
  if (!match) return "--:--";
  const date = new Date(2026, 0, 1, Number(match[1]), Number(match[2]));
  return timeFormatter.format(date);
};

export function normalizeClock(value: string): string | null {
  const compact = value.replace(/\s/g, "");
  const match = /^(\d{1,2})(?::?(\d{1,2}))?$/.exec(compact);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  if (hours > 23 || minutes > 59) return null;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

export const categories = [
  "Всё",
  "Дом",
  "Техника",
  "Одежда",
  "Хобби",
  "Детское",
  "Услуги",
  "Электронные товары",
];
